import RouteEnum from '@/Enums/Routes/Route.enum';
import { ScreenKey } from '@/Enums/Screens/ScreenKey.enum';
import GlobalFallback from '@/components/ErrorBoundary/GlobalFallback';
import Heap from '@/components/Heap/Heap';
import ZendeskWidget from '@/components/ZendeskWidget/ZendeskWidget';
import { SCREEN_LABELS, resolveLandingRoute } from '@/constants/routes';
import { isRouteAccessibile } from '@/helpers/routes';
import setAuthHeader, { getStoredAuthToken } from '@/helpers/set-auth-token';
import Advertisers from '@/pages/Advertisers';
import Agencies from '@/pages/Agencies';
import Attribution from '@/pages/Attribution';
import CampaignManagement from '@/pages/CampaignManagement';
import CreateCampaign from '@/pages/CampaignManagement/CreateCampaign';
import CampaignSubmission from '@/pages/CampaignSubmission';
import ChangeLogHistory from '@/pages/ChangeLogHistory';
import Clients from '@/pages/Clients';
import { CreativeLibrary } from '@/pages/CreativeLibrary';
import CustomDashboards from '@/pages/CustomDashboards';
import FirstPartyData from '@/pages/FirstPartyData';
import Forecaster from '@/pages/Forecaster';
import ForecasterTable from '@/pages/ForecasterTable';
import ForgotPassword from '@/pages/ForgotPassword';
import Login from '@/pages/Login';
import PartnerDataHealth from '@/pages/PartnerDataHealth';
import PdfBuilder from '@/pages/PdfBuilder';
import Pixels from '@/pages/Pixels';
import ReportBuilder from '@/pages/ReportBuilder';
import ReportingV2 from '@/pages/ReportingV2';
import Segments from '@/pages/Segments';
import SetNewPassword from '@/pages/SetNewPassword';
import Settings from '@/pages/Settings';
import SsoAuth from '@/pages/SsoAuth';
import UserManagement from '@/pages/UserManagement';
import Whitelabels from '@/pages/Whitelabels';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectAuthToken, selectIsAuthSet } from '@/store/selectors/auth.selectors';
import { selectIsAiForecasterHidden } from '@/store/selectors/currentWhitelabel.selectors';
import { selectMeLoading, selectMeScreens } from '@/store/selectors/me.selectors';
import { setImpersonationMode, setToken } from '@/store/slices/authSlice';
import {
  getCurrentWhitelabel,
  refreshCurrentWhitelabelInBackground,
  setWhitelabelFromCache,
} from '@/store/slices/currentWhitelabelSlice';
import { getCurrentUser } from '@/store/slices/meSlice';
import { ErrorBoundary } from '@sentry/react';
import { Suspense, lazy, useCallback, useEffect, useMemo } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import AppRoute from './AppRoute';
import AuthRoute from './AuthRoute';
import FallbackRoute from './FallbackRoute';
import LayoutRoute from './LayoutRoute';

const ForecasterAI = lazy(() => import('../pages/ForecasterAI/index'));
// Lazy on purpose: the legacy Reporting and Pace pages pull in the AI chat
// panel stack (@superinterface, @vidstack, @radix-ui/react-icons, micromark,
// zod — ~1.5MB of source). Imported eagerly they landed in the entry chunk, so
// every route in the app paid for them on a cold load.
const Reporting = lazy(() => import('@/pages/Reporting'));
const Pace = lazy(() => import('@/pages/Pace'));

const AppRouter = () => {
  const dispatch = useAppDispatch();
  const token = useAppSelector(selectAuthToken);
  const isAuthSet = useAppSelector(selectIsAuthSet);
  const meLoading = useAppSelector(selectMeLoading);
  const screens = useAppSelector(selectMeScreens);
  const isAiForecasterHidden = useAppSelector(selectIsAiForecasterHidden);

  // Try to get an impersonation-hop auth token from the url fragment, or fall back to
  // localStorage/sessionStorage, and set it in Redux. The fragment (not the query string)
  // carries the token because it's never sent to the server, so it never lands in
  // Cloud Run / CDN access logs the way a query param would (flank#5116, AUTH-04).
  useEffect(() => {
    const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));

    if (hashParams.has('token')) {
      const urlToken = hashParams.get('token');

      dispatch(setToken(urlToken));
      setAuthHeader(urlToken);

      // Impersonation mode logic
      if (hashParams.has('impersonationMode') && hashParams.has('redirectedFrom')) {
        dispatch(setImpersonationMode(true));
        const redirectedFrom = hashParams.get('redirectedFrom');
        if (redirectedFrom) {
          localStorage.setItem('redirectedFrom', redirectedFrom);
        }
      }
      // Remove token from the url fragment
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
      return;
    }

    // No setAuthHeader call here: the token is already sitting in whichever
    // storage it was saved to, and AxiosProvider/the redux store both read it
    // from there directly. Re-saving it — the previous code did, defaulting
    // to localStorage — would silently promote a "Remember Me"-unchecked
    // sessionStorage token back into localStorage on every page load, undoing
    // AUTH-10's fix (review, CodeRabbit, flank#5117).
    const savedToken = getStoredAuthToken();
    dispatch(setToken(savedToken));
  }, [dispatch]);

  useEffect(() => {
    const sessionStorageData = sessionStorage.getItem('whitelabel');

    if (sessionStorageData) {
      try {
        const cachedWhitelabel = JSON.parse(sessionStorageData);
        dispatch(setWhitelabelFromCache(cachedWhitelabel));

        dispatch(refreshCurrentWhitelabelInBackground());
      } catch (error) {
        console.error('Failed to parse cached whitelabel data:', error);
        dispatch(getCurrentWhitelabel());
      }
    } else {
      dispatch(getCurrentWhitelabel());
    }
  }, [dispatch]);

  useEffect(() => {
    if (token) {
      dispatch(getCurrentUser());
    }
  }, [dispatch, token]);

  const mainFallbackRoute = useMemo(() => resolveLandingRoute(screens), [screens]);

  const getAppRouteProps = useCallback(
    (route: ScreenKey, alwaysAccessible: boolean = false) => {
      return {
        token,
        meLoading,
        isAllowed: alwaysAccessible || isRouteAccessibile(screens, route),
        mainFallback: mainFallbackRoute,
        screenTitle: SCREEN_LABELS[route],
      };
    },
    [meLoading, screens, token, mainFallbackRoute],
  );

  if (!isAuthSet) {
    return <div>Loading...</div>;
  } else
    return (
      <ErrorBoundary
        fallback={({ error, resetError }) => <GlobalFallback error={error} resetErrorBoundary={resetError} />}
        showDialog
      >
        <Heap />
        <ZendeskWidget />
        <Routes>
          <Route path={RouteEnum.SSO} element={<SsoAuth />} />
          <Route element={<LayoutRoute token={token} />}>
            <Route
              path={RouteEnum.REPORTING_DASHBOARD}
              element={
                <AppRoute {...getAppRouteProps(ScreenKey.REPORTING_DASHBOARD)}>
                  <Suspense fallback={<>...</>}>
                    <Reporting />
                  </Suspense>
                </AppRoute>
              }
            />
            <Route
              path={RouteEnum.REPORTING_V2}
              element={
                <AppRoute {...getAppRouteProps(ScreenKey.REPORTING_V2)}>
                  <ReportingV2 />
                </AppRoute>
              }
            />
            <Route
              path={RouteEnum.CUSTOM_DASHBOARDS}
              element={
                <AppRoute {...getAppRouteProps(ScreenKey.CUSTOM_DASHBOARDS)}>
                  <CustomDashboards />
                </AppRoute>
              }
            />
            <Route
              path={RouteEnum.CUSTOM_DASHBOARD_DETAIL}
              element={
                <AppRoute {...getAppRouteProps(ScreenKey.CUSTOM_DASHBOARDS)}>
                  <CustomDashboards />
                </AppRoute>
              }
            />
            <Route
              path={RouteEnum.PACING}
              element={
                <AppRoute {...getAppRouteProps(ScreenKey.PACING)}>
                  <Suspense fallback={<>...</>}>
                    <Pace />
                  </Suspense>
                </AppRoute>
              }
            />
            <Route
              path={RouteEnum.CAMPAIGN_SUBMISSION}
              element={
                <AppRoute {...getAppRouteProps(ScreenKey.CAMPAIGN_SUBMISSION)}>
                  <CampaignSubmission />
                </AppRoute>
              }
            />
            <Route
              path={RouteEnum.CAMPAIGN_MANAGEMENT}
              element={
                <AppRoute {...getAppRouteProps(ScreenKey.CAMPAIGN_MANAGEMENT)}>
                  <CampaignManagement />
                </AppRoute>
              }
            />
            <Route
              path={RouteEnum.CREATE_CAMPAIGN}
              element={
                <AppRoute {...getAppRouteProps(ScreenKey.CAMPAIGN_MANAGEMENT)}>
                  <CreateCampaign />
                </AppRoute>
              }
            />
            <Route
              path={RouteEnum.USER_MANAGEMENT}
              element={
                <AppRoute {...getAppRouteProps(ScreenKey.USER_MANAGEMENT)}>
                  <UserManagement />
                </AppRoute>
              }
            />
            <Route
              path={RouteEnum.ADVERTISERS}
              element={
                <AppRoute {...getAppRouteProps(ScreenKey.ADVERTISERS)}>
                  <Advertisers />
                </AppRoute>
              }
            />
            <Route
              path={RouteEnum.AGENCIES}
              element={
                <AppRoute {...getAppRouteProps(ScreenKey.AGENCIES)}>
                  <Agencies />
                </AppRoute>
              }
            />
            <Route
              path={RouteEnum.CLIENTS}
              element={
                <AppRoute {...getAppRouteProps(ScreenKey.CLIENTS)}>
                  <Clients />
                </AppRoute>
              }
            />
            <Route
              path={RouteEnum.FORECASTER}
              element={
                <AppRoute {...getAppRouteProps(ScreenKey.FORECASTER)}>
                  <Forecaster />
                </AppRoute>
              }
            />
            <Route
              path={RouteEnum.FORECASTER_TABLE}
              element={
                <AppRoute {...getAppRouteProps(ScreenKey.FORECASTER_TABLE, true)}>
                  <ForecasterTable />
                </AppRoute>
              }
            />
            <Route
              path={RouteEnum.FORECASTER_AI}
              element={
                <AppRoute
                  {...getAppRouteProps(ScreenKey.FORECASTER_AI)}
                  isAllowed={!isAiForecasterHidden && isRouteAccessibile(screens, ScreenKey.FORECASTER_AI)}
                >
                  <Suspense fallback={<>...</>}>
                    <ForecasterAI />
                  </Suspense>
                </AppRoute>
              }
            />
            <Route
              path={RouteEnum.REPORT_BUILDER}
              element={
                <AppRoute {...getAppRouteProps(ScreenKey.REPORT_BUILDER)}>
                  <ReportBuilder />
                </AppRoute>
              }
            />
            <Route
              path={RouteEnum.PDF_BUILDER}
              element={
                <AppRoute {...getAppRouteProps(ScreenKey.PDF_BUILDER)}>
                  <PdfBuilder />
                </AppRoute>
              }
            />
            <Route
              path={RouteEnum.PIXELS}
              element={
                <AppRoute {...getAppRouteProps(ScreenKey.PIXELS)}>
                  <Pixels />
                </AppRoute>
              }
            />
            <Route
              path={RouteEnum.SEGMENTS}
              element={
                <AppRoute {...getAppRouteProps(ScreenKey.SEGMENTS)}>
                  <Segments />
                </AppRoute>
              }
            />
            <Route
              path={RouteEnum.WHITELABELS}
              element={
                <AppRoute {...getAppRouteProps(ScreenKey.WHITELABELS)}>
                  <Whitelabels />
                </AppRoute>
              }
            />
            <Route
              path={RouteEnum.CREATIVE_LIBRARY}
              element={
                <AppRoute {...getAppRouteProps(ScreenKey.CREATIVE_LIBRARY, true)}>
                  <CreativeLibrary />
                </AppRoute>
              }
            />
            <Route
              path={RouteEnum.SETTINGS}
              element={
                <AppRoute {...getAppRouteProps(ScreenKey.SETTINGS, true)}>
                  <Settings />
                </AppRoute>
              }
            />
            <Route
              path={RouteEnum.LOGIN}
              element={
                <AuthRoute token={token}>
                  <Login />
                </AuthRoute>
              }
            />
            <Route
              path={RouteEnum.FORGOT_PASSWORD}
              element={
                <AuthRoute token={token}>
                  <ForgotPassword />
                </AuthRoute>
              }
            />
            <Route
              path={RouteEnum.SET_NEW_PASSWORD}
              element={
                <AuthRoute token={token}>
                  <SetNewPassword />
                </AuthRoute>
              }
            />
            <Route
              path={RouteEnum.FIRST_PARTY_DATA}
              element={
                <AppRoute {...getAppRouteProps(ScreenKey.FIRST_PARTY_DATA)}>
                  <FirstPartyData />
                </AppRoute>
              }
            />
            <Route
              path={RouteEnum.CHANGE_LOG_HISTORY}
              element={
                <AppRoute {...getAppRouteProps(ScreenKey.CHANGE_LOG_HISTORY)}>
                  <ChangeLogHistory />
                </AppRoute>
              }
            />
            {/* Not gated by the per-account screens grant (flank#4463) — this is
                a SUPER_ADMIN-only ops tool, not a client-facing feature a whitelabel
                opts into. `alwaysAccessible: true` skips the grant check; the page
                itself checks isSuperAdmin, and the backend route is superAdminOnly. */}
            <Route
              path={RouteEnum.PARTNER_DATA_HEALTH}
              element={
                <AppRoute {...getAppRouteProps(ScreenKey.PARTNER_DATA_HEALTH, true)}>
                  <PartnerDataHealth />
                </AppRoute>
              }
            />
            <Route
              path={RouteEnum.INSIGHTS}
              element={
                <AppRoute {...getAppRouteProps(ScreenKey.INSIGHTS)}>
                  <Attribution />
                </AppRoute>
              }
            />
            {/*legacy routes*/}
            <Route path='/attribution' element={<Navigate to={RouteEnum.INSIGHTS} replace />} />
            {/*fallback*/}
            <Route
              path='*'
              element={<FallbackRoute token={token} mainFallback={mainFallbackRoute} meLoading={meLoading} />}
            />
          </Route>
        </Routes>
      </ErrorBoundary>
    );
};

export default AppRouter;
